import React, { ReactNode, useCallback, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { FormValuesProps, ListOfLandingsPages } from 'src/@types/sustainabilityPage';
import { getDefaultComponentSettings, getListOfMerchantLandingPages, getSettingsById, saveComponentSettingsApi, updateComponentSettingsApi } from 'src/api/cmsApi';
import useMerchant from 'src/hooks/useMerchant';

//
//

export type PageType = 'BUSINESS' | 'AVIATION' | 'ECOMMERCE';

export type ISettingsTlpPagesState = {
  listOfLandingPages: ListOfLandingsPages[];
  setListOfLandingPages: (list: ListOfLandingsPages[]) => void;
  isLoadingListOfLandingPages: boolean;
  existingSettingsToUpdate: any;
  setExistingSettingsToUpdate: (data: FormValuesProps) => void;
  getDefaultSettings: (type: string) => void;
  defaultSettings: FormValuesProps | null;
  saveComponentSettingsInDB: (data: FormValuesProps) => any;
  fetchSettingsByIdFromDB: (settingsId: string) => void;
  updateSettingsByIdInDB: (id: string, landingPageId: string, settings: FormValuesProps, status: boolean, domain?: string) => any;
  getTlpPages: (merchantCode: string) => void;
  updateListOfPages: boolean;
  isLoadingDefaultSettings: boolean;
  isLoadingSettingsById: boolean;
};

export const SettingsTlpContext = React.createContext<ISettingsTlpPagesState>({
  listOfLandingPages: [],
  setListOfLandingPages: () => {},
  isLoadingListOfLandingPages: true,
  existingSettingsToUpdate: null,
  setExistingSettingsToUpdate: () => {},
  defaultSettings: null,
  getDefaultSettings: () => {},
  saveComponentSettingsInDB: () => {},
  fetchSettingsByIdFromDB: () => {},
  updateSettingsByIdInDB: () => {},
  getTlpPages: () => {},
  updateListOfPages: false,
  isLoadingDefaultSettings: true,
  isLoadingSettingsById: true,
});

type SettingsTlpProviderProps = {
  children: ReactNode;
};

const extractMessageFromErrorResponse = (error:any) => {
  let errorMessage = "Something went wrong!";
  try {
    // workaround for virtual error object. 
    // reading from "errors" directly causes freeze.
    let extractedError = JSON.parse(error?.toString())?.errors[0];
    errorMessage = extractedError?.message;
    let developerMessage = extractedError?.developer_message; 
    if (developerMessage && developerMessage.startsWith("Not supported")) {
      errorMessage += " " + developerMessage;
    }
  } catch (e) {
    console.error("Failed to extract error from response: " + e)
  }
  return errorMessage;
}

export const SettingsTlpContextProvider = ({ children }: SettingsTlpProviderProps) => {
  const { merchant } = useMerchant();

  const [listOfLandingPages, setListOfLandingPages] = useState<ListOfLandingsPages[] | []>([]);
  const [isLoadingListOfLandingPages, setIsLoadingListOfLandingPages] = useState<boolean>(true);
  const [existingSettingsToUpdate, setExistingSettingsToUpdate] = useState<FormValuesProps | null>(null);
  const [updateListOfPages, setUpdateListOfPages] = useState<boolean>(true);
  const [defaultSettings, setDefaultSettings] = useState<FormValuesProps | null>(null);
  const [isLoadingDefaultSettings, setIsLoadingDefaultSettings] = useState(true);
  const [isLoadingSettingsById, setIsLoadingSettingsById] = useState(true);

  const getTlpPages = (merchantCode: string) => {
    getListOfMerchantLandingPages(merchantCode)
      .then((response: any) => {
        setListOfLandingPages(response.data?.content);
        setIsLoadingListOfLandingPages(false);
      })
      .catch((response: any) => {
        console.error('Get landing pages list', response);
      })
      .finally(() => {
        setIsLoadingListOfLandingPages(false);
      })
  };

  const getDefaultSettings = useCallback((type: string) => {
    if (merchant?.name) {
      getDefaultComponentSettings(type, merchant?.name)
        .then((response) => {
          setDefaultSettings(response.data);
        })
        .catch((response) => {
          if (response && response.errors) {
            console.log(response.errors[0]);
            enqueueSnackbar(response.errors[0]?.message, { variant: "error" });
          }
        })
        .finally(() => {
          setIsLoadingDefaultSettings(false);
        })
    }
  }, [merchant?.name]);

  const saveComponentSettingsInDB = (data: FormValuesProps) => {
    if (merchant?.code) {
      setUpdateListOfPages(true);

      return saveComponentSettingsApi(merchant.code, { ...data })
        .then((result) => {
          enqueueSnackbar('Settings are saved!');
          return result;
        })
        .catch((err) => {
          if (err.status === 409) {
            throw err;
          }
          const errorMessage = extractMessageFromErrorResponse(err);
          enqueueSnackbar(errorMessage, { variant: "error" });
        })
        .finally(() => {
          setUpdateListOfPages(false);
        })
    }
  }

  const fetchSettingsByIdFromDB = (settingsId: string) => {
    if (settingsId) {
      getSettingsById(settingsId)
        .then((response) => {
          setExistingSettingsToUpdate(response.data);
        })
        .catch((error) => {
          console.error('Get landing page settings by id', error);
        })
        .finally(() => {
          setIsLoadingSettingsById(false);
        })
    }
  }

  const updateSettingsByIdInDB = (settingsId: string, landingPageId: string, newSettings: FormValuesProps, status: boolean, domain?: string) => {
    if (merchant?.code) {
      return updateComponentSettingsApi(merchant.code, landingPageId, settingsId, newSettings, status, domain)
        .then((result) => {
          enqueueSnackbar('Settings are updated!');
          return result;
        })
        .catch((error) => {
          const errorMessage = extractMessageFromErrorResponse(error);
          enqueueSnackbar(errorMessage, { variant: "error" });
          if (error.status === 409) {
            throw error;
          }
        })
    }
  }

  return (
    <SettingsTlpContext.Provider
      value={{
        listOfLandingPages,
        setListOfLandingPages,
        isLoadingListOfLandingPages,
        existingSettingsToUpdate,
        setExistingSettingsToUpdate,
        getDefaultSettings,
        defaultSettings,
        saveComponentSettingsInDB,
        fetchSettingsByIdFromDB,
        updateSettingsByIdInDB,
        getTlpPages,
        updateListOfPages,
        isLoadingDefaultSettings,
        isLoadingSettingsById,
      }}
    >
      {children}
    </SettingsTlpContext.Provider>
  );
};
